import { useGlobalStore } from '@highlight/ipc/react'
import { HL1_TRIAL_DAYS, type Hl1MigrationOffer } from '@highlight/common'

type Hl1Perk = Hl1MigrationOffer['perk']

/**
 * The offer as the billing store holds it. BillingManager persists the gateway's JSON unchecked, so
 * a gateway on the previous deploy, or an offer persisted before it, still lacks `trialDaysByTier`
 * and `paidInHl1`. `normalizeStoredPerk` fills those in.
 */
export interface StoredHl1MigrationOffer extends Omit<Hl1MigrationOffer, 'perk'> {
  perk: Omit<Hl1Perk, 'trialDaysByTier' | 'paidInHl1'> & Partial<Pick<Hl1Perk, 'trialDaysByTier' | 'paidInHl1'>>
}

/**
 * An offer from before the per-tier trial has no `trialDaysByTier` or `paidInHl1`. Back then every
 * claim was a month, which the server still honours as a quote, and only paid 1.0 users carried a
 * monthly price or a legacy period end: a yearly or "unlimited" subscriber has no monthly figure,
 * so the period end is what marks them paid. Read the perk through this rather than off the
 * store, so the fallback stays here.
 */
export function normalizeStoredPerk(perk: StoredHl1MigrationOffer['perk']): Hl1Perk {
  return {
    ...perk,
    trialDaysByTier: perk.trialDaysByTier ?? { lite: HL1_TRIAL_DAYS, pro2: HL1_TRIAL_DAYS },
    paidInHl1: perk.paidInHl1 ?? (perk.previousMonthlyPriceUsd !== null || perk.legacyEndsAt !== null),
  }
}

/** The 1.0 → 2.0 welcome-back perk as last published by BillingManager; null until fetched. */
export function useMigrationOffer(): StoredHl1MigrationOffer | null {
  const [offer] = useGlobalStore<StoredHl1MigrationOffer | null>('billing', 'migrationOffer', null)
  return offer
}

export const hasAvailablePerk = (offer: StoredHl1MigrationOffer | null): boolean => offer?.perk.state === 'available'

export const hasClaimedPerk = (offer: StoredHl1MigrationOffer | null): boolean => offer?.perk.state === 'claimed'
